import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AnnotationPopup, { AnnotationPopupSubmit } from '../components/AnnotationPopup';
import DictationButton from '../components/DictationButton';
import PdfPage, { Highlight, SearchBbox } from '../components/PdfPage';
import MigratePieceDialog from '../components/MigratePieceDialog';
import PiecesListPanel from '../components/PiecesListPanel';
import Btn from '../components/ui/Btn';
import CounterCircle from '../components/ui/CounterCircle';
import Splitter from '../components/ui/Splitter';
import { ApiError, api } from '../lib/api';
import { noteScopeMime } from '../lib/dragDrop';
import { getDocument } from '../lib/pdf';
import { confirmAndDeletePdf } from '../lib/pdfActions';
import {
  type InventorySourcePreference,
  sortPieces,
  sortPiecesByInventory,
} from '../lib/sortPieces';
import { toast } from '../stores/toaster';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import type {
  Annotation,
  AnnotationCategory,
  Bookmark,
  DossierNote,
  ExportJobStatus,
  InventaireEntry,
  Partie,
  PartieRole,
  PdfMetadata,
  Tag,
} from '../lib/types';
import { useAuthStore } from '../stores/auth';

/** Display-name → 2-letter uppercase initials. "Maxim Töller" → "MT". */
function toInitials(name: string | null | undefined): string {
  if (!name) return '··';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '··';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/**
 * « Carton IX · farde B · pièce 14 » — never abbreviated (charter §2).
 * Reads from the annotation's own ref_* fields (those on PDF metadata
 * don't exist yet — Sprint 6 inventaires).
 */
function cotationLongAnnot(
  a: Pick<Annotation, 'ref_carton' | 'ref_sous_farde' | 'ref_piece'>
): string | null {
  const parts: string[] = [];
  if (a.ref_carton) parts.push(`Carton ${a.ref_carton}`);
  if (a.ref_sous_farde) parts.push(`farde ${a.ref_sous_farde}`);
  if (a.ref_piece) parts.push(`pièce ${a.ref_piece}`);
  return parts.length === 0 ? null : parts.join(' · ');
}

function relativeAge(iso: string): string {
  const d = new Date(iso);
  const minutes = Math.round((Date.now() - d.getTime()) / 60000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  return `il y a ${Math.round(hours / 24)} j`;
}

type Selection = {
  text: string;
  pageNumber: number;
  bboxesAtScale1: { x: number; y: number; w: number; h: number }[];
};

const CAT_META: Record<
  AnnotationCategory,
  { label: string; cardClass: string; labelClass: string }
> = {
  favorable: { label: 'FAVORABLE', cardClass: 'border-l-cat-fav', labelClass: 'text-cat-fav' },
  defavorable: { label: 'DÉFAVORABLE', cardClass: 'border-l-cat-def', labelClass: 'text-cat-def' },
  important: { label: 'IMPORTANT', cardClass: 'border-l-cat-imp', labelClass: 'text-cat-imp-text' },
  a_verifier: { label: 'À VÉRIFIER', cardClass: 'border-l-cat-ver', labelClass: 'text-cat-ver' },
};

const ROLE_LABELS_SHORT: Record<PartieRole, string> = {
  prevenu: 'prévenu',
  partie_civile: 'partie civile',
  victime: 'victime',
  temoin: 'témoin',
  enqueteur: 'enquêteur',
  expert: 'expert',
  autre: 'autre',
};

export default function PdfEditorPage() {
  const { id, pdfId, pcId, paId, odId, cfId } = useParams<{
    id: string;
    pdfId?: string;
    pcId?: string;
    paId?: string;
    odId?: string;
    cfId?: string;
  }>();
  /**
   * Le PdfEditorPage gère cinq types de sources :
   *  - PDF greffe (``pdfId``) : flux historique, full features (annotations,
   *    bookmarks, mini-dossier).
   *  - Pièce client (``pcId``) : ouvert depuis ``PiecesClientTab``.
   *  - Pièce adverse (``paId``) : ouvert depuis ``PiecesAdversesTab``
   *    (sprint F1 viewer multi-bucket, 2026-05-23).
   *  - Autre document (``odId``) : ouvert depuis ``OtherDocumentsTab``
   *    (sprint F1 phase 2, 2026-05-23). Pas d'OCR ni de bouton Mistral.
   *  - Conclusion déposée (``cfId``) : fichier de conclusion (PDF/docx)
   *    déposé au tribunal (sprint F1 phase 2, 2026-05-23). PDF only ici.
   * Le contenu binaire du PDF est commun ; seuls les endpoints API
   * et la création d'annotations diffèrent. Les features spécifiques au
   * dossier répressif (split, review-status, extract-entities full)
   * restent gated par ``sourceKind === 'pdf'``.
   */
  type SourceKind =
    | 'pdf'
    | 'piece_client'
    | 'piece_adverse'
    | 'other_document'
    | 'conclusion_file';
  const sourceKind: SourceKind = cfId
    ? 'conclusion_file'
    : odId
      ? 'other_document'
      : paId
        ? 'piece_adverse'
        : pcId
          ? 'piece_client'
          : 'pdf';
  const sourceId = pdfId ?? pcId ?? paId ?? odId ?? cfId ?? '';
  /** API base path according to the source kind (sprint F1). */
  const apiBase: string = (() => {
    switch (sourceKind) {
      case 'pdf':
        return '/pdfs';
      case 'piece_client':
        return '/pieces-client';
      case 'piece_adverse':
        return '/pieces-adverses';
      case 'other_document':
        return '/other-documents';
      case 'conclusion_file':
        return '/conclusion-files';
    }
  })();
  /** Indique si ce bucket a une chaîne OCR (PdfPageOcr rows) — utile pour
   *  gater le fallback text layer et le bouton Re-OCR Mistral. */
  const hasOcrChain =
    sourceKind === 'pdf' ||
    sourceKind === 'piece_client' ||
    sourceKind === 'piece_adverse';
  /** Indique si ce bucket supporte le bouton ✨ Re-OCR Mistral
   *  page-par-page (sprint F2). */
  const supportsReocrMistral =
    sourceKind === 'pdf' || sourceKind === 'piece_adverse';
  /** Buckets que le backend accepte reellement en annotation.
   *  AnnotationCreate (schemas.py) est `strict` et son validator
   *  _exactly_one_source exige pdf_file_id XOR piece_client_id : il n'y a
   *  PAS de piece_adverse_id cote API, malgre la colonne en base. Offrir le
   *  popup ailleurs produisait un envoi errone (l'id partait dans
   *  piece_client_id) et donc une erreur serveur. */
  const supportsAnnotations = sourceKind === 'pdf' || sourceKind === 'piece_client';
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const accessToken = useAuthStore((s) => s.accessToken);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const deepLinkConsumed = useRef<boolean>(false);

  const [pdfMetadata, setPdfMetadata] = useState<PdfMetadata | null>(null);
  const [allPdfs, setAllPdfs] = useState<PdfMetadata[]>([]);
  const [migratePdf, setMigratePdf] = useState<{ id: string; title: string } | null>(null);
  // Ordre des pièces piloté par l'inventaire du dossier (ADR-039).
  const [inventaireEntries, setInventaireEntries] = useState<InventaireEntry[]>([]);
  const [inventorySourcePref, setInventorySourcePrefState] = useState<InventorySourcePreference>(
    () => {
      if (!id) return 'auto';
      const stored = localStorage.getItem(`adr_inv_pref_${id}`);
      if (stored === 'manual' || stored === 'ai_parsed' || stored === 'auto') return stored;
      return 'auto';
    }
  );
  const setInventorySourcePref = (pref: InventorySourcePreference) => {
    setInventorySourcePrefState(pref);
    if (id) localStorage.setItem(`adr_inv_pref_${id}`, pref);
    // Re-sort immédiat de la liste affichée.
    setAllPdfs((prev) =>
      inventaireEntries.length > 0
        ? sortPiecesByInventory(prev, inventaireEntries, pref)
        : sortPieces(prev)
    );
  };
  const [splitMode, setSplitMode] = useState(false);
  const [splitDialog, setSplitDialog] = useState<{
    pageStart: number;
    firstName: string;
    secondName: string;
  } | null>(null);
  const [submittingSplit, setSubmittingSplit] = useState(false);
  // Sprint 2 §1.1 — édition inline du nom de pièce. ``null`` = pas en édition.
  const [renameDraft, setRenameDraft] = useState<string | null>(null);
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [scale, setScale] = useState<number | null>(null);
  const [autoFit, setAutoFit] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Progress de téléchargement du blob PDF chiffré : { received, total } en
  // octets. ``total`` est null si le serveur n'envoie pas Content-Length.
  // ``stage`` permet de distinguer les phases pour l'UX de chargement.
  const [loadProgress, setLoadProgress] = useState<{
    stage: 'fetching' | 'parsing' | null;
    received: number;
    total: number | null;
  }>({ stage: null, received: 0, total: null });
  // Permet de retry le chargement sans changer d'URL (incrémenter ⇒ re-run du useEffect).
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  // Sprint big-PDF F2 (2026-05-23) — bouton "Re-OCR Mistral cette page".
  // ``reocrBusy`` empêche les clics multiples. ``ocrVersion`` est bumpé
  // après chaque succès → utilisé comme suffixe de key sur <PdfPage>
  // pour forcer un re-render qui re-fetche le bbox/text layer.
  const [reocrBusy, setReocrBusy] = useState(false);
  const [ocrVersion, setOcrVersion] = useState(0);

  // Mémorise le dernier PDF consulté dans le dossier — l'onglet « Analyse »
  // de DossierDetailPage l'utilise pour réouvrir directement la bonne pièce.
  useEffect(() => {
    if (id && pdfId && typeof window !== 'undefined') {
      window.localStorage.setItem(`adr.lastPdf.${id}`, pdfId);
    }
  }, [id, pdfId]);

  // Ratio largeur PDF / total (zone PDF + aside doc travail). Persisté
  // par utilisateur (localStorage). Default = 0.6 (60% PDF).
  const SPLITTER_KEY = 'adr.analyse.pdfRatio';
  const [pdfRatio, setPdfRatio] = useState<number>(() => {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem(SPLITTER_KEY) : null;
    const v = raw ? parseFloat(raw) : NaN;
    return Number.isFinite(v) && v >= 0.3 && v <= 0.85 ? v : 0.6;
  });
  useEffect(() => {
    if (typeof window !== 'undefined') window.localStorage.setItem(SPLITTER_KEY, String(pdfRatio));
  }, [pdfRatio]);

  // U7 — mode plein écran PDF : le doc travail disparaît, le PDF prend
  // toute la largeur restante. Bouton flottant pour rouvrir l'aside.
  const [pdfFullscreen, setPdfFullscreen] = useState(false);

  // Sprint 2 §1.2 — panneau latéral liste des pièces du dossier. Persisté
  // par utilisateur ; ouvert par défaut pour répondre à l'objectif "toujours
  // voir où on en est dans la lecture du dossier".
  const PIECES_PANEL_KEY = 'adr.analyse.piecesPanelOpen';
  const [piecesPanelOpen, setPiecesPanelOpen] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    const raw = window.localStorage.getItem(PIECES_PANEL_KEY);
    return raw === null ? true : raw === '1';
  });
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(PIECES_PANEL_KEY, piecesPanelOpen ? '1' : '0');
    }
  }, [piecesPanelOpen]);

  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [notes, setNotes] = useState<DossierNote[]>([]);
  const [parties, setParties] = useState<Partie[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  // Confidence OCR par page (clé = page_number) — alimente les badges
  // ✅⚠️❌ du PageRail. Seul le mode "pdf" est servi (endpoint dédié).
  const [ocrConfidence, setOcrConfidence] = useState<Map<number, number | null>>(new Map());
  const [exportOrg, setExportOrg] = useState<'by_category' | 'by_document' | 'by_party'>(
    'by_category'
  );
  const [pieceNoteDraft, setPieceNoteDraft] = useState<string | null>(null);
  const [extractingDates, setExtractingDates] = useState(false);
  const [extractingEntities, setExtractingEntities] = useState(false);
  const [runningOcr, setRunningOcr] = useState(false);

  // Search context propagated from RechercheTab (ADR-062). Shown as a
  // dismissable banner; the page is already scrolled to via ?page=N.
  const searchContext = searchParams.get('q');
  const [searchBannerDismissed, setSearchBannerDismissed] = useState(false);
  const [searchBboxesByPage, setSearchBboxesByPage] = useState<Map<number, SearchBbox[]>>(
    new Map()
  );
  const [ocrImageWidthByPage, setOcrImageWidthByPage] = useState<Map<number, number>>(new Map());
  const [exporting, setExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState<string | null>(null);

  // ---- Load --------------------------------------------------------------

  useEffect(() => {
    if (!sourceId || !id) return;
    let cancelled = false;
    let docRef: PDFDocumentProxy | null = null;
    pageRefs.current.clear();
    deepLinkConsumed.current = false;
    setLoadError(null);
    setLoadProgress({ stage: 'fetching', received: 0, total: null });
    // Abort le fetch après 180s sans réponse complète : évite l'écran blanc
    // infini sur un PDF trop volumineux ou un OCR/conversion qui n'a jamais
    // abouti côté backend. Timeout généreux pour les très gros dossiers.
    const fetchAbort = new AbortController();
    const fetchTimeoutId = window.setTimeout(() => fetchAbort.abort(), 180_000);

    async function load() {
      try {
        let meta: PdfMetadata;
        let siblings: PdfMetadata[];
        if (sourceKind === 'pdf') {
          [meta, siblings] = await Promise.all([
            api.get<PdfMetadata>(`/pdfs/${sourceId}`),
            api.get<PdfMetadata[]>(`/dossiers/${id}/pdfs`),
          ]);
        } else if (sourceKind === 'piece_client') {
          // Piece client : adapt the response shape to PdfMetadata-like
          // so the rest of the page works unchanged.
          const pc = await api
            .get<{
              id: string;
              dossier_id: string;
              titre: string;
              page_count: number | null;
              uploaded_by: string;
              uploaded_at: string;
              has_file: boolean;
            }>(`/pieces-client/${sourceId}`)
            .catch(() => null);
          // The pieces-client API doesn't expose a GET /<id> detail
          // endpoint yet ; pull from the list endpoint and find the match.
          // (Cheap — the dossier list is bounded.)
          const list = await api.get<(typeof pc)[]>(`/dossiers/${id}/pieces-client`);
          const found = list.find((p) => p && p.id === sourceId);
          if (!found) throw new Error('piece client not found');
          if (!found.has_file) throw new Error('piece client has no file attached');
          meta = {
            id: found.id,
            dossier_id: found.dossier_id,
            filename_original: found.titre,
            file_size_bytes: 0,
            file_hash_sha256: '',
            page_count: found.page_count,
            is_scanned: null,
            ocr_status: 'pending',
            ai_extraction_status: 'pending',
            numero_pv: null,
            titre_pv: found.titre,
            date_pv: null,
            versement_id: null,
            inventaire_entry_id: null,
            uploaded_by: found.uploaded_by,
            uploaded_at: found.uploaded_at,
          };
          siblings = []; // pas de navigation sibling pour pièces client
        } else if (sourceKind === 'piece_adverse') {
          // Piece adverse (sprint F1, 2026-05-23) — schéma PieceAdverse
          // adapté en PdfMetadata pour réutiliser tout le pipeline viewer.
          const pa = await api.get<{
            id: string;
            dossier_id: string;
            filename_original: string;
            page_count: number | null;
            ocr_status: string;
            uploaded_by: string;
            uploaded_at: string;
            tenant_id?: string;
            file_size_bytes?: number;
          }>(`/pieces-adverses/${sourceId}`);
          meta = {
            id: pa.id,
            dossier_id: pa.dossier_id,
            filename_original: pa.filename_original,
            file_size_bytes: pa.file_size_bytes ?? 0,
            file_hash_sha256: '',
            page_count: pa.page_count,
            is_scanned: null,
            ocr_status: pa.ocr_status ?? 'pending',
            ai_extraction_status: 'pending',
            numero_pv: null,
            titre_pv: pa.filename_original,
            date_pv: null,
            versement_id: null,
            inventaire_entry_id: null,
            uploaded_by: pa.uploaded_by,
            uploaded_at: pa.uploaded_at,
          };
          siblings = []; // pas de navigation sibling pour pièces adverses
        } else if (sourceKind === 'other_document') {
          // OtherDocument (sprint F1 phase 2, 2026-05-23) — pas d'OCR,
          // pas de re-OCR Mistral, lecture seule.
          const od = await api.get<{
            id: string;
            dossier_id: string;
            filename_original: string;
            file_size_bytes: number;
            uploaded_by: string;
            uploaded_at: string;
            title?: string | null;
          }>(`/other-documents/${sourceId}`);
          meta = {
            id: od.id,
            dossier_id: od.dossier_id,
            filename_original: od.filename_original,
            file_size_bytes: od.file_size_bytes ?? 0,
            file_hash_sha256: '',
            page_count: null,
            is_scanned: null,
            ocr_status: 'skipped',
            ai_extraction_status: 'pending',
            numero_pv: null,
            titre_pv: od.title || od.filename_original,
            date_pv: null,
            versement_id: null,
            inventaire_entry_id: null,
            uploaded_by: od.uploaded_by,
            uploaded_at: od.uploaded_at,
          };
          siblings = [];
        } else {
          // Conclusion file (déposée) — sprint F1 phase 2 (2026-05-23).
          // Souvent .docx ; le viewer ne s'ouvre que sur PDF (sinon le
          // backend renverra un mime non-PDF que pdf.js refusera de
          // charger — afficher message d'erreur côté UI dans ce cas).
          const cf = await api.get<{
            id: string;
            dossier_id: string;
            filename_original: string;
            file_size_bytes: number;
            content_type: string | null;
            uploaded_by: string;
            uploaded_at: string;
            title?: string | null;
          }>(`/conclusion-files/${sourceId}`);
          meta = {
            id: cf.id,
            dossier_id: cf.dossier_id,
            filename_original: cf.filename_original,
            file_size_bytes: cf.file_size_bytes ?? 0,
            file_hash_sha256: '',
            page_count: null,
            is_scanned: null,
            ocr_status: 'skipped',
            ai_extraction_status: 'pending',
            numero_pv: null,
            titre_pv: cf.title || cf.filename_original,
            date_pv: null,
            versement_id: null,
            inventaire_entry_id: null,
            uploaded_by: cf.uploaded_by,
            uploaded_at: cf.uploaded_at,
          };
          siblings = [];
        }
        if (cancelled) return;
        setPdfMetadata(meta);

        // Fetch inventaire entries pour ordonner les pièces selon
        // l'inventaire choisi (ADR-039) — fallback sortPieces si erreur.
        let entries: InventaireEntry[] = [];
        try {
          entries = await api.get<InventaireEntry[]>(`/dossiers/${id}/inventaire`);
        } catch {
          entries = [];
        }
        if (cancelled) return;
        setInventaireEntries(entries);
        setAllPdfs(
          entries.length > 0
            ? sortPiecesByInventory(siblings, entries, inventorySourcePref)
            : sortPieces(siblings)
        );

        const annFilter =
          sourceKind === 'pdf' ? `pdf_file_id=${sourceId}` : `piece_client_id=${sourceId}`;
        const noteFilter = annFilter; // identique : même schéma de scope
        const [anns, ns, pts, tgs, bms] = await Promise.all([
          api.get<Annotation[]>(`/dossiers/${meta.dossier_id}/annotations?${annFilter}`),
          api.get<DossierNote[]>(`/dossiers/${meta.dossier_id}/notes?${noteFilter}`),
          api.get<Partie[]>(`/dossiers/${meta.dossier_id}/parties`),
          api.get<Tag[]>(`/dossiers/${meta.dossier_id}/tags`),
          api.get<Bookmark[]>(`/dossiers/${meta.dossier_id}/bookmarks`),
        ]);
        if (cancelled) return;
        setAnnotations(anns);
        setNotes(ns);
        setParties(pts);
        setTags(tgs);
        setBookmarks(bms);

        // Charger les confidences OCR par page (PDF greffe uniquement —
        // l'endpoint dédié n'existe pas encore pour pieces_client). En
        // background : pas bloquant pour le rendu PDF initial.
        if (sourceKind === 'pdf') {
          api
            .get<{ page_number: number; confidence: number | null }[]>(
              `/pdfs/${sourceId}/ocr-confidences`
            )
            .then((rows) => {
              if (cancelled) return;
              const m = new Map<number, number | null>();
              for (const r of rows) m.set(r.page_number, r.confidence);
              setOcrConfidence(m);
            })
            .catch(() => {
              /* OCR pas encore lancé sur cette pièce — silent */
            });
        }

        const streamUrl = `/api/v1${apiBase}/${sourceId}/stream`;
        const resp = await fetch(streamUrl, {
          headers: { Authorization: `Bearer ${accessToken}` },
          signal: fetchAbort.signal,
        });
        if (!resp.ok) {
          const text = await resp.text().catch(() => '');
          throw new Error(`stream HTTP ${resp.status}${text ? ` — ${text.slice(0, 200)}` : ''}`);
        }
        // Lecture chunked du body pour exposer une vraie progression à l'UI.
        // ``Content-Length`` est lu côté navigateur ; il peut être absent si
        // un proxy compresse la réponse — dans ce cas ``total`` reste null
        // mais ``received`` continue de monter.
        const contentLength = resp.headers.get('content-length');
        const total = contentLength ? parseInt(contentLength, 10) : null;
        if (!resp.body) throw new Error('stream sans body');
        const reader = resp.body.getReader();
        const chunks: Uint8Array[] = [];
        let received = 0;
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          if (value) {
            chunks.push(value);
            received += value.byteLength;
            if (!cancelled) setLoadProgress({ stage: 'fetching', received, total });
          }
        }
        if (cancelled) return;
        // Concat des chunks en un seul ArrayBuffer pour pdf.js.
        const buf = new Uint8Array(received);
        let offset = 0;
        for (const c of chunks) {
          buf.set(c, offset);
          offset += c.byteLength;
        }
        setLoadProgress({ stage: 'parsing', received, total: total ?? received });
        docRef = await getDocument({ data: buf }).promise;
        if (cancelled) {
          await docRef.destroy();
          return;
        }
        setDoc(docRef);
        setPageCount(docRef.numPages);
        setLoadProgress({ stage: null, received: 0, total: null });
      } catch (err) {
        const name = (err as Error)?.name;
        if (name === 'AbortError') {
          setLoadError(
            'Le chargement a été interrompu (timeout 180s). La pièce est peut-être trop volumineuse, ou le serveur ne répond pas.'
          );
        } else {
          setLoadError(
            err instanceof ApiError
              ? (err.problem.detail ?? err.problem.title)
              : (err as Error).message
          );
        }
        setLoadProgress({ stage: null, received: 0, total: null });
      }
    }

    void load();
    return () => {
      cancelled = true;
      window.clearTimeout(fetchTimeoutId);
      fetchAbort.abort();
      if (docRef) void docRef.destroy();
    };
  }, [sourceId, sourceKind, id, accessToken, loadAttempt]);

  // ---- Search-match bboxes (Sprint 4-4) ---------------------------------
  // Fetch OCR words for the deep-linked page when arriving from RechercheTab,
  // keep only those matching ``searchContext``, and pass them to PdfPage.

  useEffect(() => {
    if (!searchContext || !sourceId) return;
    const deep = parseInt(searchParams.get('page') ?? '', 10);
    if (!Number.isFinite(deep) || deep <= 0) return;
    let cancelled = false;
    const path =
      `${apiBase}/${sourceId}/ocr/pages/${deep}`;

    type OcrPagePayload = {
      page_number: number;
      image_size: { w: number; h: number };
      words: { text: string; x: number; y: number; w: number; h: number }[];
    };

    const stripDiacritics = (s: string): string => s.normalize('NFD').replace(/[̀-ͯ]/g, '');
    const norm = (s: string): string => stripDiacritics(s).toLowerCase();
    const terms = norm(searchContext)
      .split(/\s+/)
      .filter((t) => t.length >= 2)
      // Drop common search operators users might leave in the query bar.
      .filter((t) => !['or', 'and', '-'].includes(t));

    void api
      .get<OcrPagePayload>(path)
      .then((page) => {
        if (cancelled || terms.length === 0) return;
        const matches: SearchBbox[] = page.words
          .filter((w) => {
            const t = norm(w.text);
            return terms.some((needle) => t.includes(needle));
          })
          .map((w) => ({ x: w.x, y: w.y, w: w.w, h: w.h }));
        setSearchBboxesByPage((m) => {
          const next = new Map(m);
          next.set(page.page_number, matches);
          return next;
        });
        setOcrImageWidthByPage((m) => {
          const next = new Map(m);
          next.set(page.page_number, page.image_size.w);
          return next;
        });
      })
      .catch(() => {
        // Silently ignore — page may not have OCR yet (no_ocr_page).
      });

    return () => {
      cancelled = true;
    };
  }, [searchContext, sourceId, sourceKind, searchParams]);

  // ---- Fit-to-width ------------------------------------------------------

  const computeFitScale = useMemo(
    () => async () => {
      if (!doc || !scrollerRef.current) return;
      const page = await doc.getPage(1);
      const viewport = page.getViewport({ scale: 1 });
      const w = scrollerRef.current.clientWidth - 32;
      if (w > 0) setScale(w / viewport.width);
    },
    [doc]
  );

  useEffect(() => {
    if (autoFit) void computeFitScale();
  }, [doc, autoFit, computeFitScale]);

  useEffect(() => {
    if (!autoFit) return;
    const h = () => void computeFitScale();
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [autoFit, computeFitScale]);

  // ---- Track current visible page ---------------------------------------
  // The "current page" is the one whose top-edge is closest to the scroller's
  // viewport top from above. This is what the user is reading right now and
  // what « + Note » should attach itself to (ADR-035).

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller || pageCount === 0) return;
    let rafId = 0;
    const update = () => {
      const scrollerTop = scroller.getBoundingClientRect().top;
      let best = 1;
      let bestDelta = Infinity;
      pageRefs.current.forEach((el, n) => {
        const wrapper = el.parentElement ?? el; // outer div with data-page
        const wrapperTop = wrapper.getBoundingClientRect().top;
        const delta = scrollerTop - wrapperTop;
        // Closest page that has scrolled past the top (delta >= 0) wins; if
        // none has, fall back to the page nearest the top (smallest |delta|).
        if (delta >= -8 && delta < bestDelta) {
          bestDelta = delta;
          best = n;
        }
      });
      setCurrentPage(best);
    };
    const onScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(update);
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    update();
    return () => {
      scroller.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(rafId);
    };
  }, [doc, pageCount, scale]);

  const scrollToPage = (n: number) => {
    const el = pageRefs.current.get(n);
    if (!el || !scrollerRef.current) return;
    const wrapper = el.parentElement ?? el;
    const scroller = scrollerRef.current;
    const scrollerRect = scroller.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();
    const target = scroller.scrollTop + (wrapperRect.top - scrollerRect.top) - 8;
    scroller.scrollTo({ top: target, behavior: 'smooth' });
  };

  // ---- Selection capture (multi-rect, scale=1) ---------------------------

  const reloadAnnotations = async () => {
    if (!pdfMetadata || !sourceId) return;
    const filter = sourceKind === 'pdf' ? `pdf_file_id=${sourceId}` : `piece_client_id=${sourceId}`;
    const anns = await api.get<Annotation[]>(
      `/dossiers/${pdfMetadata.dossier_id}/annotations?${filter}`
    );
    setAnnotations(anns);
  };

  const reloadNotes = async () => {
    if (!pdfMetadata || !sourceId) return;
    const param = sourceKind === 'pdf' ? `pdf_file_id=${sourceId}` : `piece_client_id=${sourceId}`;
    const ns = await api.get<DossierNote[]>(`/dossiers/${pdfMetadata.dossier_id}/notes?${param}`);
    setNotes(ns);
  };

  const submitPieceNote = async (content: string, pageHint: number | null) => {
    if (!pdfMetadata || !sourceId) return;
    const body: Record<string, unknown> = {
      content: content.trim(),
    };
    if (sourceKind === 'pdf') {
      body.pdf_file_id = sourceId;
    } else {
      body.piece_client_id = sourceId;
    }
    if (pageHint) body.page_number = pageHint;
    try {
      await api.post<DossierNote>(`/dossiers/${pdfMetadata.dossier_id}/notes`, body);
      setPieceNoteDraft(null);
      await reloadNotes();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    }
  };

  const updatePieceNote = async (noteId: string, newContent: string) => {
    await api.patch<DossierNote>(`/notes/${noteId}`, { content: newContent });
    await reloadNotes();
  };

  const deletePieceNote = async (noteId: string) => {
    await api.delete(`/notes/${noteId}`);
    await reloadNotes();
  };

  /** Drag-drop reorder of piece-scoped notes — same scope (page) only. */
  const reorderNote = async (draggedId: string, targetId: string) => {
    if (draggedId === targetId) return;
    const dragged = notes.find((n) => n.id === draggedId);
    const target = notes.find((n) => n.id === targetId);
    if (!dragged || !target) return;
    if (dragged.pdf_file_id !== target.pdf_file_id || dragged.page_number !== target.page_number) {
      return;
    }
    const siblings = notes
      .filter(
        (n) =>
          n.id !== draggedId &&
          n.pdf_file_id === target.pdf_file_id &&
          n.page_number === target.page_number
      )
      .sort((a, b) => (a.sort_key ?? 0) - (b.sort_key ?? 0));
    const idx = siblings.findIndex((n) => n.id === targetId);
    if (idx < 0) return;
    const targetKey = target.sort_key ?? 0;
    const prev = idx > 0 ? siblings[idx - 1] : null;
    const newKey = prev != null ? ((prev.sort_key ?? 0) + targetKey) / 2 : targetKey - 1.0;
    await api.patch<DossierNote>(`/notes/${draggedId}`, { sort_key: newKey });
    await reloadNotes();
  };

  const onMouseUp = () => {
    setTimeout(() => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || sel.rangeCount === 0 || !scale) {
        setSelection(null);
        return;
      }
      const text = sel.toString().trim();
      if (text.length < 3) {
        setSelection(null);
        return;
      }
      const range = sel.getRangeAt(0);
      const allRects = Array.from(range.getClientRects()).filter(
        (r) => r.width > 0 && r.height > 0
      );
      if (allRects.length === 0) {
        setSelection(null);
        return;
      }
      const anchor = sel.anchorNode?.parentElement?.closest('[data-page]') as HTMLElement | null;
      const pageNumberAttr = anchor?.getAttribute('data-page');
      if (!pageNumberAttr) return;
      const pageNumber = parseInt(pageNumberAttr, 10);
      const pageEl = pageRefs.current.get(pageNumber);
      const pageRect = pageEl?.getBoundingClientRect();
      if (!pageRect) return;
      const bboxesAtScale1 = allRects.map((r) => ({
        x: (r.left - pageRect.left) / scale,
        y: (r.top - pageRect.top) / scale,
        w: r.width / scale,
        h: r.height / scale,
      }));
      setSelection({ text, pageNumber, bboxesAtScale1 });
    }, 0);
  };

  const submitAnnotation = async ({ category, partie_ids }: AnnotationPopupSubmit) => {
    // 2026-09-10 : on LEVE au lieu de sortir en silence. Le popup attrape et
    // affiche : sans ca, le clic disparaissait dans le vide.
    if (!selection) {
      throw new Error('Selection perdue - resurlignez le passage.');
    }
    if (!sourceId) {
      throw new Error('Piece introuvable - rechargez la page.');
    }
    if (!supportsAnnotations) {
      throw new Error("Ce type de piece n'accepte pas encore d'annotation.");
    }
    const body: Record<string, unknown> = {
      page_number: selection.pageNumber,
      category,
      text_extract: selection.text,
      coords_json: selection.bboxesAtScale1,
    };
    if (sourceKind === 'pdf') {
      body.pdf_file_id = sourceId;
    } else {
      body.piece_client_id = sourceId;
    }
    if (partie_ids.length > 0) body.partie_ids = partie_ids;
    await api.post<Annotation>('/annotations', body);
    await reloadAnnotations();
    window.getSelection()?.removeAllRanges();
    setSelection(null);
  };

  const deleteAnnotation = async (annotationId: string) => {
    await api.delete(`/annotations/${annotationId}`);
    await reloadAnnotations();
  };

  const updateComment = async (annotationId: string, newComment: string) => {
    await api.patch<Annotation>(`/annotations/${annotationId}`, { comment: newComment });
    await reloadAnnotations();
  };

  const updatePartyLinks = async (annotationId: string, partyIds: string[]) => {
    await api.patch<Annotation>(`/annotations/${annotationId}`, { partie_ids: partyIds });
    await reloadAnnotations();
  };

  const updateTagLinks = async (annotationId: string, tagIds: string[]) => {
    await api.patch<Annotation>(`/annotations/${annotationId}`, { tag_ids: tagIds });
    await reloadAnnotations();
  };

  const reloadTags = async () => {
    if (!pdfMetadata) return;
    const rows = await api.get<Tag[]>(`/dossiers/${pdfMetadata.dossier_id}/tags`);
    setTags(rows);
  };

  const updateTagColor = async (tagId: string, color: string) => {
    await api.patch<Tag>(`/tags/${tagId}`, { color });
    await reloadTags();
  };

  const createAndLinkTag = async (
    annotationId: string,
    label: string,
    color?: string
  ): Promise<string | null> => {
    if (!pdfMetadata) return null;
    try {
      const body: Record<string, unknown> = { label };
      if (color) body.color = color;
      const tag = await api.post<Tag>(`/dossiers/${pdfMetadata.dossier_id}/tags`, body);
      await reloadTags();
      const ann = annotations.find((a) => a.id === annotationId);
      const next = [...(ann?.linked_tag_ids ?? []), tag.id];
      await updateTagLinks(annotationId, next);
      return tag.id;
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
      return null;
    }
  };

  const extractDates = async () => {
    if (!sourceId) return;
    setExtractingDates(true);
    try {
      const path =
        sourceKind === 'pdf'
          ? `/pdfs/${sourceId}/extract-dates`
          : `/pieces-client/${sourceId}/extract-dates`;
      const summary = await api.post<{
        status: string;
        inserted?: number;
        pages_scanned?: number;
      }>(path);
      if (summary.status === 'no_ocr') {
        toast.info('Aucun texte OCR sur cette pièce — extraction impossible.');
      } else if (summary.status === 'ok') {
        toast.success(
          `Dates IA : ${summary.inserted ?? 0} évènement${(summary.inserted ?? 0) > 1 ? 's' : ''} ajouté${(summary.inserted ?? 0) > 1 ? 's' : ''} à la timeline (à vérifier).`
        );
      } else {
        toast.error(`Extraction : ${summary.status}`);
      }
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setExtractingDates(false);
    }
  };

  // ---- Bookmarks (marqueurs page ou pièce entière) -----------------------

  /**
   * Trouve le bookmark exact (page ou pièce entière) sur la source courante.
   * `pageNumber=null` = marqueur sur la pièce entière.
   */
  const findBookmark = (pageNumber: number | null): Bookmark | undefined =>
    bookmarks.find(
      (b) =>
        (sourceKind === 'pdf' ? b.pdf_file_id === sourceId : b.piece_client_id === sourceId) &&
        (b.page_number ?? null) === pageNumber
    );

  const toggleBookmark = async (pageNumber: number | null) => {
    if (!pdfMetadata) return;
    const existing = findBookmark(pageNumber);
    try {
      if (existing) {
        await api.delete(`/bookmarks/${existing.id}`);
        setBookmarks((bs) => bs.filter((b) => b.id !== existing.id));
        toast.info(
          pageNumber === null ? 'Marqueur de pièce retiré' : `Page ${pageNumber} démarquée`
        );
      } else {
        const created = await api.post<Bookmark>(`/dossiers/${pdfMetadata.dossier_id}/bookmarks`, {
          ...(sourceKind === 'pdf' ? { pdf_file_id: sourceId } : { piece_client_id: sourceId }),
          page_number: pageNumber,
        });
        setBookmarks((bs) => [...bs, created]);
        toast.success(
          pageNumber === null
            ? 'Pièce marquée — visible dans le mini-dossier'
            : `Page ${pageNumber} marquée`
        );
      }
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    }
  };

  const toggleReviewStatus = async () => {
    if (!pdfMetadata || sourceKind !== 'pdf') return;
    const next: 'non_vue' | 'analysee' =
      pdfMetadata.review_status === 'analysee' ? 'non_vue' : 'analysee';
    try {
      const updated = await api.patch<PdfMetadata>(`/pdfs/${sourceId}/review-status`, {
        review_status: next,
      });
      setPdfMetadata(updated);
      toast.success(next === 'analysee' ? 'Pièce marquée comme analysée' : 'Statut réinitialisé');
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    }
  };

  const runOcr = async () => {
    if (!sourceId) return;
    setRunningOcr(true);
    try {
      const path =
        sourceKind === 'pdf' ? `/pdfs/${sourceId}/ocr` : `/pieces-client/${sourceId}/ocr`;
      const summary = await api.post<{
        status: string;
        pages?: number;
        avg_confidence?: number | null;
        detail?: string;
      }>(path);
      if (summary.status === 'ok') {
        const conf =
          summary.avg_confidence != null
            ? ` · confiance moy. ${(summary.avg_confidence * 100).toFixed(0)}%`
            : '';
        toast.success(
          `OCR : ${summary.pages ?? 0} page${(summary.pages ?? 0) > 1 ? 's' : ''} indexée${(summary.pages ?? 0) > 1 ? 's' : ''}${conf}.`
        );
      } else if (summary.status === 'ocr_unavailable') {
        toast.error(`OCR indisponible : ${summary.detail ?? 'Tesseract manquant'}`);
      } else {
        toast.error(`OCR : ${summary.status}`);
      }
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setRunningOcr(false);
    }
  };

  const submitRename = async (raw: string) => {
    if (!pdfMetadata || sourceKind !== 'pdf') return;
    const trimmed = raw.trim();
    if (!trimmed || trimmed === pdfMetadata.filename_original) {
      setRenameDraft(null);
      return;
    }
    try {
      const updated = await api.patch<PdfMetadata>(`/pdfs/${sourceId}`, {
        filename_original: trimmed,
      });
      setPdfMetadata(updated);
      setAllPdfs((prev) => {
        const next = prev.map((p) => (p.id === updated.id ? updated : p));
        return inventaireEntries.length > 0
          ? sortPiecesByInventory(next, inventaireEntries, inventorySourcePref)
          : sortPieces(next);
      });
      toast.success('Pièce renommée');
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setRenameDraft(null);
    }
  };

  const submitSplit = async () => {
    if (!splitDialog || !pdfMetadata || sourceKind !== 'pdf') return;
    const { pageStart, firstName, secondName } = splitDialog;
    if (!firstName.trim() || !secondName.trim()) {
      toast.error('Les deux noms sont requis');
      return;
    }
    setSubmittingSplit(true);
    try {
      const resp = await api.post<{ first: PdfMetadata; second: PdfMetadata }>(
        `/pdfs/${sourceId}/split`,
        {
          split_at_page: pageStart,
          first_name: firstName.trim(),
          second_name: secondName.trim(),
        }
      );
      toast.success('Pièce découpée — OCR en cours sur les deux fragments');
      setSplitDialog(null);
      setSplitMode(false);
      navigate(`/dossiers/${id}/pdfs/${resp.first.id}`);
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setSubmittingSplit(false);
    }
  };

  const extractEntities = async () => {
    if (!sourceId) return;
    setExtractingEntities(true);
    try {
      const path =
        sourceKind === 'pdf'
          ? `/pdfs/${sourceId}/extract-entities`
          : `/pieces-client/${sourceId}/extract-entities`;
      const summary = await api.post<{
        status: string;
        inserted?: number;
        pages_scanned?: number;
      }>(path);
      if (summary.status === 'no_ocr') {
        toast.info('Aucun texte OCR — extraction impossible.');
      } else if (summary.status === 'ok') {
        toast.success(
          `Parties IA : ${summary.inserted ?? 0} candidat${(summary.inserted ?? 0) > 1 ? 's' : ''} ajouté${(summary.inserted ?? 0) > 1 ? 's' : ''} (à valider dans Parties).`
        );
      } else {
        toast.error(`Extraction : ${summary.status}`);
      }
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setExtractingEntities(false);
    }
  };

  const exportThisPiece = async () => {
    if (!id || !sourceId || !pdfMetadata) return;
    setExporting(true);
    setExportStatus('Génération…');
    try {
      const body: Record<string, unknown> = {
        organization: exportOrg,
      };
      if (sourceKind === 'pdf') {
        body.pdf_file_ids = [sourceId];
      } else {
        body.piece_client_ids = [sourceId];
      }
      const trigger = await api.post<ExportJobStatus>(`/dossiers/${id}/export`, body);
      const jobId = trigger.job_id;
      for (let i = 0; i < 60; i += 1) {
        const status = await api.get<ExportJobStatus>(`/exports/${jobId}`);
        if (status.status === 'done' && status.download_url) {
          const resp = await fetch(status.download_url, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          const blob = await resp.blob();
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          const safeName = pdfMetadata.filename_original.replace(/\.[^.]+$/, '');
          link.download = `adr-${safeName}-notes.docx`;
          link.click();
          URL.revokeObjectURL(url);
          setExportStatus('Téléchargé');
          break;
        }
        if (status.status === 'failed') {
          setExportStatus(`Échec : ${status.error ?? 'inconnu'}`);
          break;
        }
        await new Promise((r) => setTimeout(r, 500));
      }
    } catch (err) {
      setExportStatus(err instanceof ApiError ? `Erreur : ${err.problem.title}` : 'Erreur réseau');
    } finally {
      setExporting(false);
    }
  };

  // ---- Highlights derived from annotations -------------------------------

  const highlightsByPage = useMemo(() => {
    const m = new Map<number, Highlight[]>();
    for (const a of annotations) {
      const arr = m.get(a.page_number) ?? [];
      for (const c of a.coords_json) {
        arr.push({ x: c.x, y: c.y, w: c.w, h: c.h, category: a.category, annotationId: a.id });
      }
      m.set(a.page_number, arr);
    }
    return m;
  }, [annotations]);

  // Per-page treated state: a page is « traitée » as soon as it carries
  // at least one annotation OR a piece-scoped note (ADR-035).
  const treatedPages = useMemo(() => {
    const s = new Set<number>();
    for (const a of annotations) s.add(a.page_number);
    for (const n of notes) if (n.page_number) s.add(n.page_number);
    return s;
  }, [annotations, notes]);

  // ---- Counters by category ---------------------------------------------

  const counters = useMemo(() => {
    const c: Record<AnnotationCategory, number> = {
      favorable: 0,
      defavorable: 0,
      important: 0,
      a_verifier: 0,
    };
    for (const a of annotations) c[a.category] += 1;
    return c;
  }, [annotations]);

  // ---- Merged annotations + piece-scoped notes ordered by page ----------

  type RightItem = { kind: 'annotation'; data: Annotation } | { kind: 'note'; data: DossierNote };

  const mergedItems: RightItem[] = useMemo(() => {
    const xs: RightItem[] = [
      ...annotations.map((a): RightItem => ({ kind: 'annotation', data: a })),
      ...notes.map((n): RightItem => ({ kind: 'note', data: n })),
    ];
    const key = (it: RightItem): [number, number, number] => {
      if (it.kind === 'annotation') {
        // Tri dans l'ordre d'apparition dans la page (Y du premier rect),
        // pas dans l'ordre de création. Une annotation rajoutée plus tard
        // sur du texte plus haut dans la page se replace donc au bon endroit.
        const firstY = it.data.coords_json[0]?.y ?? 0;
        return [it.data.page_number, firstY, new Date(it.data.created_at).getTime()];
      }
      return [
        it.data.page_number ?? 0,
        it.data.sort_key ?? 0,
        new Date(it.data.created_at).getTime(),
      ];
    };
    xs.sort((a, b) => {
      const ka = key(a);
      const kb = key(b);
      for (let i = 0; i < ka.length; i += 1) if (ka[i]! !== kb[i]!) return ka[i]! - kb[i]!;
      return 0;
    });
    return xs;
  }, [annotations, notes]);

  // ---- Navigation between pieces ----------------------------------------

  const currentIndex = pdfId ? allPdfs.findIndex((p) => p.id === pdfId) : -1;
  const goTo = (newId: string) => {
    if (!id) return;
    navigate(`/dossiers/${id}/pieces/${newId}`);
  };

  // Sprint 2 §14 — suppression depuis le panneau lateral. Si on supprime
  // la piece active on saute sur la voisine (suivante puis precedente),
  // sinon on retombe sur le dossier.
  const onDeleteFromPanel = async (target: PdfMetadata) => {
    if (!(await confirmAndDeletePdf(target.id, target.filename_original))) return;
    const remaining = allPdfs.filter((p) => p.id !== target.id);
    setAllPdfs(remaining);
    if (target.id !== pdfId) return;
    if (!id) return;
    const idx = allPdfs.findIndex((p) => p.id === target.id);
    const next = remaining[idx] ?? remaining[idx - 1] ?? null;
    if (next) {
      navigate(`/dossiers/${id}/pieces/${next.id}`);
    } else {
      navigate(`/dossiers/${id}`);
    }
  };

  // ---- Render ------------------------------------------------------------

  if (loadError) {
    return (
      <div className="space-y-4">
        <Link
          to={id ? `/dossiers/${id}` : '/dossiers'}
          className="text-sm text-text-secondary hover:underline"
        >
          ← Retour au dossier
        </Link>
        <div className="space-y-3 rounded-md border border-cat-def/40 bg-cat-def-pastel px-4 py-3 text-sm text-cat-def">
          <p className="font-medium">Impossible d'ouvrir cette pièce</p>
          <p className="text-text-primary">{loadError}</p>
          {pdfMetadata && (
            <p className="text-xs text-text-tertiary">
              <span className="font-mono">{pdfMetadata.filename_original}</span>
              {pdfMetadata.file_size_bytes
                ? ` · ${(pdfMetadata.file_size_bytes / 1024 / 1024).toFixed(1)} Mo`
                : ''}
              {pdfMetadata.page_count ? ` · ${pdfMetadata.page_count} pages` : ''}
            </p>
          )}
          <button
            type="button"
            onClick={() => {
              setLoadError(null);
              setLoadAttempt((n) => n + 1);
            }}
            className="rounded-md border border-cat-def/40 bg-paper-elevated px-3 py-1.5 text-xs font-medium text-cat-def hover:bg-paper-card"
          >
            ↻ Réessayer
          </button>
        </div>
      </div>
    );
  }

  // Loading screen explicite tant que le PDF n'est pas prêt — sinon l'utilisateur
  // voit un écran vide pendant le download d'un gros blob.
  if (!doc && loadProgress.stage) {
    const mb = (loadProgress.received / 1024 / 1024).toFixed(1);
    const totalMb = loadProgress.total ? (loadProgress.total / 1024 / 1024).toFixed(1) : null;
    const pct = loadProgress.total
      ? Math.min(99, Math.round((loadProgress.received / loadProgress.total) * 100))
      : null;
    return (
      <div className="space-y-3">
        <Link
          to={id ? `/dossiers/${id}` : '/dossiers'}
          className="text-sm text-text-secondary hover:underline"
        >
          ← Retour au dossier
        </Link>
        <div className="rounded-md border border-rule bg-paper-elevated px-4 py-6 text-sm">
          <p className="font-medium text-text-primary">
            {loadProgress.stage === 'fetching' ? 'Chargement de la pièce…' : 'Lecture du PDF…'}
          </p>
          <p className="mt-1 text-xs text-text-tertiary">
            {loadProgress.stage === 'fetching'
              ? totalMb
                ? `${mb} / ${totalMb} Mo`
                : `${mb} Mo téléchargés`
              : 'Décodage par pdf.js…'}
          </p>
          {pct !== null && (
            <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-paper-subtle">
              <div
                className="h-full rounded-full bg-accent transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {searchContext && !searchBannerDismissed && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-cat-imp/40 bg-cat-imp-pastel px-3 py-2 text-[12px]">
          <span className="text-cat-imp-text">
            Recherche : <span className="font-mono">« {searchContext} »</span> · page mise en
            évidence depuis l'onglet Recherche.
          </span>
          <span className="ml-auto flex items-center gap-2">
            <span className="text-text-secondary">
              <kbd className="rounded border border-rule-strong bg-paper-elevated px-1.5 py-[1px] font-mono text-[10px]">
                Ctrl+F
              </kbd>{' '}
              pour trouver le passage exact dans le PDF.
            </span>
            <button
              onClick={() => setSearchBannerDismissed(true)}
              className="rounded p-0.5 text-text-tertiary hover:bg-paper-subtle hover:text-text-primary"
              aria-label="Fermer"
            >
              ✕
            </button>
          </span>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-rule pb-3">
        <div className="flex min-w-0 items-center gap-2 text-[13px] text-text-secondary">
          <Link
            to={id ? `/dossiers/${id}` : '/dossiers'}
            className="text-text-link hover:underline"
          >
            ← Retour au dossier
          </Link>
          <span className="text-text-tertiary">›</span>
          {pdfMetadata && (
            <span className="flex items-center gap-1.5 min-w-0">
              {renameDraft !== null && sourceKind === 'pdf' ? (
                <input
                  autoFocus
                  type="text"
                  value={renameDraft}
                  onChange={(e) => setRenameDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void submitRename(renameDraft);
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      setRenameDraft(null);
                    }
                  }}
                  onBlur={() => void submitRename(renameDraft)}
                  className="min-w-0 flex-1 max-w-md rounded border border-accent bg-paper-elevated px-1.5 py-0.5 font-mono text-[12px] text-text-primary outline-none ring-1 ring-accent"
                  aria-label="Nouveau nom de la pièce"
                />
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      if (sourceKind === 'pdf') setRenameDraft(pdfMetadata.filename_original);
                    }}
                    disabled={sourceKind !== 'pdf'}
                    className="font-mono text-[12px] text-text-primary truncate text-left rounded px-1 -mx-1 hover:bg-paper-subtle disabled:cursor-default disabled:hover:bg-transparent"
                    title={
                      sourceKind === 'pdf'
                        ? 'Cliquer pour renommer la pièce'
                        : pdfMetadata.filename_original
                    }
                  >
                    {pdfMetadata.filename_original}
                  </button>
                  {sourceKind === 'pdf' && (
                    <>
                      <button
                        type="button"
                        onClick={() => setRenameDraft(pdfMetadata.filename_original)}
                        className="rounded p-0.5 text-text-tertiary hover:bg-paper-subtle hover:text-text-primary"
                        title="Renommer la pièce"
                        aria-label="Renommer la pièce"
                      >
                        ✏
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setMigratePdf({
                            id: pdfMetadata.id,
                            title: pdfMetadata.filename_original,
                          })
                        }
                        className="rounded border border-accent/30 bg-accent-light px-1.5 py-0.5 text-[11px] text-accent-text transition hover:bg-accent hover:text-paper-elevated"
                        title="Ranger cette pièce dans un autre bucket"
                        aria-label="Ranger la pièce"
                      >
                        ↗ Ranger
                      </button>
                    </>
                  )}
                </>
              )}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {sourceKind === 'pdf' ? (
            <Btn
              variant={piecesPanelOpen ? 'dark' : 'secondary'}
              size="sm"
              onClick={() => setPiecesPanelOpen((v) => !v)}
              title={
                piecesPanelOpen ? 'Masquer la liste des pièces' : 'Afficher la liste des pièces'
              }
            >
              {piecesPanelOpen ? '⮜' : '☰'} Pièces · {currentIndex >= 0 ? currentIndex + 1 : '?'}/
              {allPdfs.length}
            </Btn>
          ) : (
            <span className="rounded-md bg-paper-subtle px-2 py-1 text-[11px] font-medium uppercase tracking-[0.3px] text-text-secondary">
              Pièce client
            </span>
          )}
          <span className="mx-2 hidden h-5 w-px bg-rule md:block" />
          {/* Bookmarks (marqueurs page / pièce entière) */}
          {(() => {
            const pageMark = findBookmark(currentPage);
            const pieceMark = findBookmark(null);
            return (
              <>
                <Btn
                  variant={pageMark ? 'dark' : 'ghost'}
                  size="sm"
                  onClick={() => void toggleBookmark(currentPage)}
                  title={
                    pageMark
                      ? `Retirer le marqueur de la page ${currentPage}`
                      : `Marquer la page ${currentPage}`
                  }
                >
                  {pageMark ? '🔖 Page' : '🏷 Page'}
                </Btn>
                <Btn
                  variant={pieceMark ? 'dark' : 'ghost'}
                  size="sm"
                  onClick={() => void toggleBookmark(null)}
                  title={
                    pieceMark
                      ? 'Retirer le marqueur de la pièce entière'
                      : 'Marquer la pièce entière (mini-dossier)'
                  }
                >
                  {pieceMark ? '🔖 Pièce' : '🏷 Pièce'}
                </Btn>
                {supportsReocrMistral && (
                  <Btn
                    variant="ghost"
                    size="sm"
                    disabled={reocrBusy}
                    onClick={async () => {
                      if (!sourceId || !currentPage) return;
                      setReocrBusy(true);
                      try {
                        const res = await api.post<{
                          text_length: number;
                          cost_eur: number;
                        }>(
                          `${apiBase}/${sourceId}/pages/${currentPage}/reocr-mistral`,
                          {},
                        );
                        toast.success(
                          `Page ${currentPage} re-OCRisée Mistral (${res.text_length} car., ${res.cost_eur.toFixed(4)} €)`,
                        );
                        setOcrVersion((v) => v + 1);
                      } catch (err) {
                        toast.error(
                          err instanceof ApiError ? err.message : 'Erreur Re-OCR Mistral',
                        );
                      } finally {
                        setReocrBusy(false);
                      }
                    }}
                    title={`Re-OCR Mistral de la page ${currentPage} (garantie qualité, ~0.001 €)`}
                  >
                    {reocrBusy ? '…' : '✨ OCR'}
                  </Btn>
                )}
                <Btn
                  variant={pdfFullscreen ? 'dark' : 'ghost'}
                  size="sm"
                  onClick={() => setPdfFullscreen((v) => !v)}
                  title={pdfFullscreen ? 'Réafficher le doc de travail' : 'Plein écran PDF'}
                >
                  {pdfFullscreen ? '⇤' : '⛶'}
                </Btn>
                {sourceKind === 'pdf' && pdfMetadata && (
                  <Btn
                    variant={pdfMetadata.review_status === 'analysee' ? 'dark' : 'ghost'}
                    size="sm"
                    onClick={() => void toggleReviewStatus()}
                    title={
                      pdfMetadata.review_status === 'analysee'
                        ? 'Repasser en « non analysée »'
                        : 'Marquer la pièce comme analysée'
                    }
                  >
                    {pdfMetadata.review_status === 'analysee' ? '✅ Analysée' : '✓ Marquer'}
                  </Btn>
                )}
                {sourceKind === 'pdf' && pdfMetadata && pageCount > 1 && (
                  <Btn
                    variant={splitMode ? 'dark' : 'ghost'}
                    size="sm"
                    onClick={() => setSplitMode((v) => !v)}
                    title={
                      splitMode
                        ? 'Quitter le mode découpe'
                        : 'Découper le PDF en plusieurs pièces — clique entre 2 pages'
                    }
                  >
                    {splitMode ? '✂ Annuler' : '✂ Découper'}
                  </Btn>
                )}
                <span className="mx-2 hidden h-5 w-px bg-rule md:block" />
              </>
            );
          })()}
          <Btn
            variant="ghost"
            size="sm"
            onClick={() => {
              setAutoFit(false);
              setScale((s) => Math.max(0.5, (s ?? 1) - 0.25));
            }}
            aria-label="Zoom out"
          >
            −
          </Btn>
          <Btn
            variant={autoFit ? 'dark' : 'secondary'}
            size="sm"
            onClick={() => {
              setAutoFit(true);
              void computeFitScale();
            }}
            title="Adapter à la largeur"
          >
            Ajuster
          </Btn>
          <span className="font-mono text-[11px] text-text-tertiary w-12 text-center">
            {scale ? `${Math.round(scale * 100)}%` : '…'}
          </span>
          <Btn
            variant="ghost"
            size="sm"
            onClick={() => {
              setAutoFit(false);
              setScale((s) => Math.min(3, (s ?? 1) + 0.25));
            }}
            aria-label="Zoom in"
          >
            +
          </Btn>
        </div>
      </div>

      <div
        className="relative grid gap-2"
        style={{
          height: 'calc(100vh - 130px)',
          gridTemplateColumns: (() => {
            // Layout dynamique : panneau pieces (1.2) → PageRail → PDF → Splitter → Doc travail.
            // Le panneau pieces et le doc travail sont indépendants l'un de l'autre.
            const piecesCol = sourceKind === 'pdf' && piecesPanelOpen ? '260px ' : '';
            const tail = pdfFullscreen ? '1fr' : `${pdfRatio}fr 4px ${1 - pdfRatio}fr`;
            return `${piecesCol}56px ${tail}`;
          })(),
        }}
      >
        {sourceKind === 'pdf' && piecesPanelOpen && (
          <PiecesListPanel
            pieces={allPdfs}
            activeId={pdfId}
            annotations={annotations}
            onPick={(targetId) => {
              if (targetId !== pdfId) goTo(targetId);
            }}
            onClose={() => setPiecesPanelOpen(false)}
            onDelete={(target) => void onDeleteFromPanel(target)}
            onMigrate={(target) => setMigratePdf({ id: target.id, title: target.filename_original })}
            inventorySourcePref={inventaireEntries.length > 0 ? inventorySourcePref : undefined}
            onChangeInventorySource={
              inventaireEntries.length > 0 ? setInventorySourcePref : undefined
            }
          />
        )}
        {/* Page rail — treated/untreated marker per page (ADR-035) */}
        <PageRail
          pageCount={pageCount}
          currentPage={currentPage}
          treatedPages={treatedPages}
          bookmarkedPages={
            new Set(
              bookmarks
                .filter(
                  (b) =>
                    (sourceKind === 'pdf'
                      ? b.pdf_file_id === sourceId
                      : b.piece_client_id === sourceId) && b.page_number !== null
                )
                .map((b) => b.page_number as number)
            )
          }
          ocrConfidence={ocrConfidence}
          onJump={scrollToPage}
        />

        {/* PDF column */}
        <div
          ref={scrollerRef}
          onMouseUp={onMouseUp}
          className="relative overflow-auto rounded-lg border border-rule bg-paper-card p-4"
        >
          {!doc && <p className="p-6 text-sm text-text-secondary">Chargement du PDF…</p>}
          {doc &&
            scale &&
            Array.from({ length: pageCount }, (_, i) => (
              <Fragment key={`${pdfId}-${i + 1}-${scale.toFixed(2)}-v${ocrVersion}`}>
                {splitMode && i > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      const base = pdfMetadata?.filename_original ?? '';
                      setSplitDialog({
                        pageStart: i + 1,
                        firstName: base,
                        secondName: '',
                      });
                    }}
                    className="my-3 w-full rounded-md border-2 border-dashed border-amber-500 bg-amber-50 py-3 text-[13px] font-medium text-amber-900 hover:bg-amber-100"
                    title={`Découper ici — la page ${i + 1} commence la 2e pièce`}
                  >
                    ✂ Découper ici · page {i + 1} → 2e pièce
                  </button>
                )}
                <PdfPage
                  doc={doc}
                  pageNumber={i + 1}
                  scale={scale}
                  highlights={highlightsByPage.get(i + 1) ?? []}
                  searchHighlights={searchBboxesByPage.get(i + 1) ?? []}
                  ocrImageWidth={ocrImageWidthByPage.get(i + 1) ?? 0}
                  {...(hasOcrChain ? { ocrSourceBaseUrl: `${apiBase}/${sourceId}` } : {})}
                  onLayoutReady={(pageNumber, container) => {
                    pageRefs.current.set(pageNumber, container);
                    if (deepLinkConsumed.current) return;

                    // Deep-link 1 : ?annotation=ID — scroll vers la page de
                    // l'annotation + flash visuel sur le highlight (clic
                    // depuis Doc travail). Plus précis que ?page=N.
                    const annotId = searchParams.get('annotation');
                    if (annotId) {
                      const target = annotations.find((a) => a.id === annotId);
                      if (target && target.page_number === pageNumber) {
                        deepLinkConsumed.current = true;
                        requestAnimationFrame(() => {
                          scrollToPage(pageNumber);
                          // Délai pour laisser le scroll se poser avant le flash.
                          setTimeout(() => {
                            const els = container.querySelectorAll(
                              `[data-annotation-id="${annotId}"]`
                            );
                            els.forEach((el) => {
                              el.classList.add('hl-flash');
                              setTimeout(() => el.classList.remove('hl-flash'), 1800);
                            });
                          }, 250);
                        });
                        setSearchParams(
                          (prev) => {
                            const next = new URLSearchParams(prev);
                            next.delete('annotation');
                            next.delete('page');
                            return next;
                          },
                          { replace: true }
                        );
                        return;
                      }
                    }

                    // Deep-link 2 : ?page=N (ADR-040) — fallback simple.
                    const deep = parseInt(searchParams.get('page') ?? '', 10);
                    if (Number.isFinite(deep) && deep === pageNumber) {
                      deepLinkConsumed.current = true;
                      requestAnimationFrame(() => scrollToPage(pageNumber));
                      setSearchParams(
                        (prev) => {
                          const next = new URLSearchParams(prev);
                          next.delete('page');
                          return next;
                        },
                        { replace: true }
                      );
                    }
                  }}
                />
              </Fragment>
            ))}

          {selection && supportsAnnotations && (
            <AnnotationPopup
              selectedText={selection.text}
              parties={parties}
              onSubmit={submitAnnotation}
              onCancel={() => {
                setSelection(null);
                window.getSelection()?.removeAllRanges();
              }}
            />
          )}
        </div>

        {/* Splitter draggable entre PDF et doc de travail (L1) — caché en fullscreen */}
        {!pdfFullscreen && <Splitter ratio={pdfRatio} onRatioChange={setPdfRatio} />}

        {/* Document de travail — caché en fullscreen, bouton flottant pour rouvrir */}
        {pdfFullscreen && (
          <button
            type="button"
            onClick={() => setPdfFullscreen(false)}
            className="absolute right-4 top-4 z-20 rounded-md border border-rule-strong bg-paper-elevated px-3 py-2 text-[12px] font-medium text-text-primary shadow-md hover:bg-paper-card"
            title="Rouvrir le document de travail"
          >
            ⇤ Doc travail
          </button>
        )}
        {!pdfFullscreen && (
          <aside className="flex flex-col overflow-hidden rounded-lg border border-rule bg-paper p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="font-serif text-[16px] font-medium text-text-primary">
                Document de travail
              </span>
              <span className="text-[12px] text-text-tertiary">
                {annotations.length} entrée{annotations.length > 1 ? 's' : ''}
              </span>
            </div>

            <div className="mb-3 flex items-center justify-between">
              <div className="flex gap-1.5">
                <CounterCircle category="favorable" count={counters.favorable} title="Favorables" />
                <CounterCircle
                  category="defavorable"
                  count={counters.defavorable}
                  title="Défavorables"
                />
                <CounterCircle category="important" count={counters.important} title="Importants" />
                <CounterCircle
                  category="a_verifier"
                  count={counters.a_verifier}
                  title="À vérifier"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <Btn
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    pieceNoteDraft === null ? setPieceNoteDraft('') : setPieceNoteDraft(null)
                  }
                  title={
                    currentPage
                      ? `Ajouter une note attachée à la page ${currentPage}`
                      : 'Ajouter une note libre attachée à cette pièce'
                  }
                >
                  + Note {currentPage ? `p. ${currentPage}` : ''}
                </Btn>
                <select
                  value={exportOrg}
                  onChange={(e) =>
                    setExportOrg(e.target.value as 'by_category' | 'by_document' | 'by_party')
                  }
                  className="rounded-md border border-rule-strong bg-paper-elevated px-2 py-1 text-xs"
                  title="Organisation du Word généré"
                >
                  <option value="by_category">Par catégorie</option>
                  <option value="by_document">Par pièce (audience)</option>
                  <option value="by_party">Par partie (plaidoirie)</option>
                </select>
                <Btn
                  variant="ghost"
                  size="sm"
                  onClick={() => void runOcr()}
                  disabled={runningOcr}
                  title="Lancer l'OCR Tesseract sur cette pièce — alimente la recherche full-text et l'extraction IA"
                >
                  {runningOcr ? 'OCR…' : '↯ OCR'}
                </Btn>
                <Btn
                  variant="ghost"
                  size="sm"
                  onClick={() => void extractDates()}
                  disabled={extractingDates}
                  title="Extraire automatiquement les dates de cette pièce vers la timeline (à vérifier)"
                >
                  {extractingDates ? 'IA…' : '↯ Dates'}
                </Btn>
                <Btn
                  variant="ghost"
                  size="sm"
                  onClick={() => void extractEntities()}
                  disabled={extractingEntities}
                  title="Extraire automatiquement les parties (prévenu, témoin, …) — exige ANTHROPIC_API_KEY"
                >
                  {extractingEntities ? 'IA…' : '↯ Parties'}
                </Btn>
                <Btn
                  variant="primary"
                  size="sm"
                  onClick={exportThisPiece}
                  disabled={exporting || (annotations.length === 0 && notes.length === 0)}
                  title={
                    annotations.length === 0 && notes.length === 0
                      ? 'Rien à exporter'
                      : 'Exporter Word — annotations + notes de cette pièce'
                  }
                >
                  {exporting ? 'Export…' : 'Exporter'}
                </Btn>
              </div>
            </div>
            {exportStatus && <p className="mb-2 text-xs text-text-tertiary">{exportStatus}</p>}

            {pieceNoteDraft !== null && (
              <div className="mb-2 space-y-2 rounded-md border border-accent/40 bg-accent-lighter p-3">
                <div className="text-[10px] font-semibold uppercase tracking-[1px] text-accent-text">
                  Nouvelle note {currentPage ? `· p. ${currentPage}` : '· (sans page)'}
                </div>
                <textarea
                  value={pieceNoteDraft}
                  onChange={(e) => setPieceNoteDraft(e.target.value)}
                  rows={3}
                  autoFocus
                  placeholder="Note libre…"
                  className="w-full rounded-sm border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[13px]"
                />
                <div className="flex justify-end gap-1.5">
                  <Btn
                    variant="ghost"
                    size="sm"
                    onClick={() => setPieceNoteDraft(null)}
                    disabled={!pieceNoteDraft}
                  >
                    Annuler
                  </Btn>
                  <Btn
                    variant="primary"
                    size="sm"
                    onClick={() => void submitPieceNote(pieceNoteDraft ?? '', currentPage || null)}
                    disabled={!pieceNoteDraft.trim()}
                  >
                    Créer la note
                  </Btn>
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto">
              {annotations.length === 0 && notes.length === 0 ? (
                <p className="p-2 text-sm text-text-secondary">
                  Aucune annotation. Sélectionne un passage dans le PDF, ou clique « + Note » pour
                  démarrer.
                </p>
              ) : (
                <ul className="space-y-2.5">
                  {mergedItems.map((it) =>
                    it.kind === 'annotation' ? (
                      <AnnotationCard
                        key={`a-${it.data.id}`}
                        annotation={it.data}
                        pdfFilenameRef={
                          pdfMetadata?.numero_pv ?? pdfMetadata?.filename_original ?? ''
                        }
                        cotation={cotationLongAnnot(it.data)}
                        parties={parties}
                        tags={tags}
                        onDelete={() => deleteAnnotation(it.data.id)}
                        onCommentChange={(c) => updateComment(it.data.id, c)}
                        onPartyLinksChange={(ids) => updatePartyLinks(it.data.id, ids)}
                        onTagLinksChange={(ids) => updateTagLinks(it.data.id, ids)}
                        onCreateAndLinkTag={(label) => createAndLinkTag(it.data.id, label)}
                        onUpdateTagColor={updateTagColor}
                      />
                    ) : (
                      <PieceNoteCard
                        key={`n-${it.data.id}`}
                        note={it.data}
                        onUpdate={(c) => updatePieceNote(it.data.id, c)}
                        onDelete={() => deletePieceNote(it.data.id)}
                        onReorder={reorderNote}
                      />
                    )
                  )}
                </ul>
              )}
            </div>
          </aside>
        )}
      </div>
      {splitDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-[480px] max-w-[90vw] rounded-lg border border-rule bg-paper p-5 shadow-xl">
            <h2 className="mb-1 font-serif text-[18px] font-medium text-text-primary">
              Découper la pièce
            </h2>
            <p className="mb-4 text-[13px] text-text-secondary">
              La 2<sup>e</sup> pièce commencera à la <strong>page {splitDialog.pageStart}</strong>.
              Donne un nom à chaque morceau (les annotations et marqueurs seront répartis
              automatiquement).
            </p>
            <label className="mb-3 block">
              <span className="mb-1 block text-[12px] font-medium text-text-secondary">
                Nom de la 1<sup>re</sup> pièce (pages 1 à {splitDialog.pageStart - 1})
              </span>
              <input
                type="text"
                value={splitDialog.firstName}
                onChange={(e) =>
                  setSplitDialog((d) => (d ? { ...d, firstName: e.target.value } : d))
                }
                className="w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-sm"
                autoFocus
              />
            </label>
            <label className="mb-4 block">
              <span className="mb-1 block text-[12px] font-medium text-text-secondary">
                Nom de la 2<sup>e</sup> pièce (page {splitDialog.pageStart} à fin)
              </span>
              <input
                type="text"
                value={splitDialog.secondName}
                onChange={(e) =>
                  setSplitDialog((d) => (d ? { ...d, secondName: e.target.value } : d))
                }
                className="w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-sm"
              />
            </label>
            <div className="flex justify-end gap-2">
              <Btn
                variant="ghost"
                size="sm"
                onClick={() => setSplitDialog(null)}
                disabled={submittingSplit}
              >
                Annuler
              </Btn>
              <Btn
                variant="primary"
                size="sm"
                onClick={() => void submitSplit()}
                disabled={
                  submittingSplit || !splitDialog.firstName.trim() || !splitDialog.secondName.trim()
                }
              >
                {submittingSplit ? 'Découpe…' : 'Découper'}
              </Btn>
            </div>
          </div>
        </div>
      )}

      {migratePdf && (
        <MigratePieceDialog
          pdfId={migratePdf.id}
          pdfTitle={migratePdf.title}
          onClose={() => setMigratePdf(null)}
          onMigrated={() => {
            // Pièce migrée → on quitte la page (la pièce n'existe plus
            // comme PdfFile). Navigation vers le tab Pièces du dossier.
            if (id) {
              navigate(`/dossiers/${id}/dossier/pieces`);
            }
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PageRail — slim left column with one pastille per page, marking treated
// (≥1 annotation or piece-scoped note) vs untreated. Click to scroll.
// ---------------------------------------------------------------------------

function PageRail({
  pageCount,
  currentPage,
  treatedPages,
  bookmarkedPages,
  ocrConfidence,
  onJump,
}: {
  pageCount: number;
  currentPage: number;
  treatedPages: Set<number>;
  bookmarkedPages: Set<number>;
  ocrConfidence: Map<number, number | null>;
  onJump: (n: number) => void;
}) {
  const railRef = useRef<HTMLDivElement>(null);
  const buttonsRef = useRef<Map<number, HTMLButtonElement>>(new Map());

  // Auto-scroll the rail so currentPage stays visible (ADR-044).
  // We use rail-only scrollBy (NOT scrollIntoView) so the document
  // body never moves while the user reads the PDF.
  useEffect(() => {
    const rail = railRef.current;
    const btn = buttonsRef.current.get(currentPage);
    if (!rail || !btn) return;
    const railRect = rail.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    const margin = 24;
    const offsetTop = btnRect.top - railRect.top;
    const offsetBot = btnRect.bottom - railRect.bottom;
    if (offsetTop < margin) {
      rail.scrollBy({ top: offsetTop - margin, behavior: 'smooth' });
    } else if (offsetBot > -margin) {
      rail.scrollBy({ top: offsetBot + margin, behavior: 'smooth' });
    }
  }, [currentPage]);

  if (pageCount === 0) {
    return <div className="rounded-lg border border-rule bg-paper-elevated" />;
  }
  const treatedCount = treatedPages.size;
  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-rule bg-paper-elevated">
      <header className="border-b border-rule px-1 py-1.5 text-center font-mono text-[10px] uppercase tracking-[0.3px] text-text-tertiary">
        {treatedCount}/{pageCount}
      </header>
      <div ref={railRef} className="flex-1 overflow-y-auto py-1.5">
        <ul className="flex flex-col items-center gap-0.5 px-1">
          {Array.from({ length: pageCount }, (_, i) => {
            const n = i + 1;
            const isCurrent = n === currentPage;
            const isTreated = treatedPages.has(n);
            const isBookmarked = bookmarkedPages.has(n);
            const conf = ocrConfidence.get(n);
            // Badge OCR : ✅ ≥0.85, ⚠️ 0.70-0.85, ❌ <0.70. Pas de badge
            // si la page n'a pas encore d'OCR (conf=undefined).
            let ocrBadge: string | null = null;
            let ocrTitle = '';
            if (conf !== undefined) {
              if (conf === null) {
                ocrBadge = null; // OCR ran but Tesseract didn't measure
              } else if (conf >= 0.85) {
                ocrBadge = '✓';
                ocrTitle = ` · OCR ${Math.round(conf * 100)}%`;
              } else if (conf >= 0.7) {
                ocrBadge = '!';
                ocrTitle = ` · OCR ${Math.round(conf * 100)}% (à vérifier)`;
              } else {
                ocrBadge = '×';
                ocrTitle = ` · OCR ${Math.round(conf * 100)}% (faible)`;
              }
            }
            const ocrBadgeClass =
              conf === undefined
                ? ''
                : conf !== null && conf >= 0.85
                  ? 'text-cat-fav'
                  : conf !== null && conf >= 0.7
                    ? 'text-cat-imp-text'
                    : 'text-cat-def';
            return (
              <li key={n} className="w-full">
                <button
                  ref={(el) => {
                    if (el) buttonsRef.current.set(n, el);
                    else buttonsRef.current.delete(n);
                  }}
                  onClick={() => onJump(n)}
                  className={`relative flex h-6 w-full items-center justify-center rounded-sm font-mono text-[10px] transition ${
                    isCurrent
                      ? 'bg-accent-light text-accent-text font-semibold'
                      : isTreated
                        ? 'text-accent-text hover:bg-paper-subtle'
                        : 'text-text-tertiary hover:bg-paper-subtle'
                  }`}
                  title={`Page ${n}${isTreated ? ' · traitée' : ''}${isBookmarked ? ' · marquée' : ''}${ocrTitle}`}
                  aria-current={isCurrent ? 'page' : undefined}
                >
                  {n}
                  {isBookmarked && (
                    <span className="absolute left-0.5 top-0.5 text-[9px] leading-none" aria-hidden>
                      🔖
                    </span>
                  )}
                  {ocrBadge && (
                    <span
                      className={`absolute bottom-0 left-0.5 text-[8px] font-bold leading-none ${ocrBadgeClass}`}
                      aria-hidden
                    >
                      {ocrBadge}
                    </span>
                  )}
                  {isTreated && (
                    <span
                      className={`absolute right-1 top-1 inline-block h-1.5 w-1.5 rounded-full ${
                        isCurrent ? 'bg-accent-text' : 'bg-accent'
                      }`}
                      aria-hidden
                    />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AnnotationCard — border-left coloured + Georgia italic citation (M1 spec)
// ---------------------------------------------------------------------------

function AnnotationCard({
  annotation: a,
  pdfFilenameRef,
  cotation,
  parties,
  tags,
  onDelete,
  onCommentChange,
  onPartyLinksChange,
  onTagLinksChange,
  onCreateAndLinkTag,
  onUpdateTagColor,
}: {
  annotation: Annotation;
  pdfFilenameRef: string;
  cotation: string | null;
  parties: Partie[];
  tags: Tag[];
  onDelete: () => Promise<void>;
  onCommentChange: (newComment: string) => Promise<void>;
  onPartyLinksChange: (partyIds: string[]) => Promise<void>;
  onTagLinksChange: (tagIds: string[]) => Promise<void>;
  onCreateAndLinkTag: (label: string, color?: string) => Promise<string | null>;
  onUpdateTagColor: (tagId: string, color: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(a.comment ?? '');
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [partyMenuOpen, setPartyMenuOpen] = useState(false);
  const [tagMenuOpen, setTagMenuOpen] = useState(false);
  const [newTagDraft, setNewTagDraft] = useState<string | null>(null);
  const [newTagColor, setNewTagColor] = useState<string>('');

  const meta = CAT_META[a.category];
  const initials = toInitials(a.creator_display_name);
  const partyById = useMemo(() => {
    const m = new Map<string, Partie>();
    for (const p of parties) m.set(p.id, p);
    return m;
  }, [parties]);
  const tagById = useMemo(() => {
    const m = new Map<string, Tag>();
    for (const t of tags) m.set(t.id, t);
    return m;
  }, [tags]);
  const linkedParties = a.linked_party_ids
    .map((id) => partyById.get(id))
    .filter((p): p is Partie => Boolean(p));
  const unlinkedParties = parties.filter((p) => !a.linked_party_ids.includes(p.id));
  const linkedTags = a.linked_tag_ids
    .map((id) => tagById.get(id))
    .filter((t): t is Tag => Boolean(t));
  const unlinkedTags = tags.filter((t) => !a.linked_tag_ids.includes(t.id));

  const togglePartyLink = async (partyId: string) => {
    const next = a.linked_party_ids.includes(partyId)
      ? a.linked_party_ids.filter((x) => x !== partyId)
      : [...a.linked_party_ids, partyId];
    await onPartyLinksChange(next);
  };

  const toggleTagLink = async (tagId: string) => {
    const next = a.linked_tag_ids.includes(tagId)
      ? a.linked_tag_ids.filter((x) => x !== tagId)
      : [...a.linked_tag_ids, tagId];
    await onTagLinksChange(next);
  };

  const save = async () => {
    setBusy(true);
    try {
      await onCommentChange(draft.trim());
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await onDelete();
    } finally {
      setBusy(false);
    }
  };

  return (
    <li
      className={`group rounded-sm border-l-4 bg-paper-elevated px-3.5 py-3 transition hover:bg-paper-card ${meta.cardClass}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className={`text-[10px] font-semibold uppercase tracking-[1px] ${meta.labelClass}`}>
          {meta.label}
        </div>
        {confirmDel ? (
          <div className="flex items-center gap-1 text-[11px]">
            <Btn variant="ghost" size="sm" onClick={() => setConfirmDel(false)} disabled={busy}>
              Annuler
            </Btn>
            <button
              onClick={() => void remove()}
              disabled={busy}
              className="rounded-md bg-cat-def px-2 py-1 text-[11px] font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              Supprimer
            </button>
          </div>
        ) : (
          <button
            onClick={() => setConfirmDel(true)}
            className="rounded p-1 text-text-tertiary opacity-0 transition group-hover:opacity-100 hover:bg-cat-def-pastel hover:text-cat-def"
            aria-label="Supprimer"
            title="Supprimer cette annotation"
          >
            ✕
          </button>
        )}
      </div>

      <p className="mt-1.5 font-serif text-[13px] italic leading-snug text-text-primary">
        « {a.text_extract} »
      </p>

      {editing ? (
        <div className="mt-2 space-y-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            className="w-full rounded-sm border border-rule-strong bg-paper-card px-2 py-1.5 text-[12px]"
            placeholder="Commentaire (clic 🎤 pour dicter)…"
            autoFocus
          />
          <div className="flex items-center justify-between gap-1">
            <DictationButton
              size="sm"
              onTranscript={(text) =>
                setDraft((prev) => (prev ? prev.trimEnd() + ' ' : '') + text)
              }
              title="Dicter le commentaire"
            />
            <div className="flex gap-1">
              <Btn
                variant="ghost"
                size="sm"
                onClick={() => {
                  setDraft(a.comment ?? '');
                  setEditing(false);
                }}
                disabled={busy}
              >
                Annuler
              </Btn>
              <Btn variant="primary" size="sm" onClick={() => void save()} disabled={busy}>
                {busy ? '…' : 'Enregistrer'}
              </Btn>
            </div>
          </div>
        </div>
      ) : a.comment ? (
        <p className="mt-1 text-[12px] italic text-text-secondary">{a.comment}</p>
      ) : (
        <button
          onClick={() => setEditing(true)}
          className="mt-1 text-[11px] text-text-tertiary opacity-0 transition group-hover:opacity-100 hover:underline"
        >
          + ajouter un commentaire
        </button>
      )}

      {/* Party chips — Sprint 2-2 / ADR-036 */}
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {linkedParties.map((p) => (
          <button
            key={p.id}
            onClick={() => void togglePartyLink(p.id)}
            className={`group/chip inline-flex items-center gap-1 rounded-pill px-2 py-[2px] text-[10px] font-medium ${
              p.is_client
                ? 'bg-accent-light text-accent-text'
                : 'bg-paper-subtle text-text-secondary'
            } hover:bg-cat-def-pastel hover:text-cat-def`}
            title={`${ROLE_LABELS_SHORT[p.role]} — cliquer pour délier`}
          >
            <span>
              {p.prenom ? `${p.prenom[0]!}. ` : ''}
              {p.nom}
            </span>
            <span className="opacity-0 transition group-hover/chip:opacity-100">✕</span>
          </button>
        ))}
        {unlinkedParties.length > 0 && (
          <div className="relative">
            <button
              onClick={() => setPartyMenuOpen((v) => !v)}
              className="rounded-pill border border-dashed border-rule-strong bg-paper-elevated px-2 py-[2px] text-[10px] font-medium text-text-tertiary hover:border-accent hover:text-accent-text"
              title="Lier une partie à cet extrait"
            >
              + partie
            </button>
            {partyMenuOpen && (
              <ul className="absolute left-0 top-full z-20 mt-1 max-h-56 w-56 overflow-y-auto rounded-md border border-rule bg-paper-elevated shadow-md">
                {unlinkedParties.map((p) => (
                  <li key={p.id}>
                    <button
                      onClick={async () => {
                        setPartyMenuOpen(false);
                        await togglePartyLink(p.id);
                      }}
                      className="block w-full px-2.5 py-1.5 text-left text-[12px] hover:bg-paper-subtle"
                    >
                      <span className="font-medium text-text-primary">
                        {p.prenom ? `${p.prenom} ` : ''}
                        {p.nom}
                      </span>
                      <span className="ml-1.5 text-[10px] uppercase tracking-[0.3px] text-text-tertiary">
                        {ROLE_LABELS_SHORT[p.role]}
                      </span>
                      {p.is_client && (
                        <span className="ml-1.5 text-[10px] font-semibold text-accent-text">
                          client
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Tag chips — ADR-045. Color picker inline (raffinement) : un swatch à gauche
          du label ouvre l'input natif <input type="color"> pour éditer la couleur du
          tag existant ; le label reste cliquable pour délier. */}
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        {linkedTags.map((t) => (
          <span
            key={t.id}
            className="group/chip inline-flex items-center gap-1 rounded-pill bg-paper-card px-2 py-[2px] text-[10px] font-medium text-text-primary border border-rule-strong hover:bg-cat-def-pastel hover:text-cat-def hover:border-cat-def/40"
            style={
              t.color
                ? { backgroundColor: t.color, color: '#fff', borderColor: 'transparent' }
                : undefined
            }
          >
            <label
              className="relative inline-block h-2.5 w-2.5 cursor-pointer rounded-full border border-white/40 hover:scale-110"
              style={{ backgroundColor: t.color ?? '#cbd5f5' }}
              title="Modifier la couleur du tag"
            >
              <input
                type="color"
                value={t.color ?? '#cbd5f5'}
                onChange={(e) => void onUpdateTagColor(t.id, e.target.value)}
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                aria-label={`Couleur du tag ${t.label}`}
              />
            </label>
            <button
              onClick={() => void toggleTagLink(t.id)}
              className="inline-flex items-center gap-1"
              title="Cliquer pour délier ce tag"
            >
              <span>#{t.label}</span>
              <span className="opacity-0 transition group-hover/chip:opacity-100">✕</span>
            </button>
          </span>
        ))}
        {(unlinkedTags.length > 0 || tags.length === 0) && (
          <div className="relative">
            <button
              onClick={() => {
                setTagMenuOpen((v) => !v);
                setNewTagDraft(null);
              }}
              className="rounded-pill border border-dashed border-rule-strong bg-paper-elevated px-2 py-[2px] text-[10px] font-medium text-text-tertiary hover:border-accent hover:text-accent-text"
              title="Ajouter un tag à cet extrait"
            >
              + tag
            </button>
            {tagMenuOpen && (
              <div className="absolute left-0 top-full z-20 mt-1 w-56 overflow-hidden rounded-md border border-rule bg-paper-elevated shadow-md">
                <ul className="max-h-40 overflow-y-auto">
                  {unlinkedTags.map((t) => (
                    <li key={t.id}>
                      <button
                        onClick={async () => {
                          setTagMenuOpen(false);
                          await toggleTagLink(t.id);
                        }}
                        className="block w-full px-2.5 py-1.5 text-left text-[12px] hover:bg-paper-subtle"
                      >
                        <span className="font-medium text-text-primary">#{t.label}</span>
                      </button>
                    </li>
                  ))}
                  {unlinkedTags.length === 0 && (
                    <li className="px-2.5 py-1.5 text-[11px] italic text-text-tertiary">
                      Aucun tag existant. Crée-en un ci-dessous.
                    </li>
                  )}
                </ul>
                {newTagDraft === null ? (
                  <button
                    onClick={() => {
                      setNewTagDraft('');
                      setNewTagColor('');
                    }}
                    className="block w-full border-t border-rule px-2.5 py-1.5 text-left text-[11px] text-accent-text hover:bg-paper-subtle"
                  >
                    + Créer un tag…
                  </button>
                ) : (
                  <div className="space-y-1.5 border-t border-rule p-2">
                    <div className="flex items-center gap-1.5">
                      <input
                        autoFocus
                        value={newTagDraft}
                        onChange={(e) => setNewTagDraft(e.target.value)}
                        placeholder="ex. argument-clé"
                        className="flex-1 rounded-sm border border-rule-strong bg-paper-elevated px-2 py-1 text-[12px]"
                        onKeyDown={async (e) => {
                          if (e.key === 'Enter' && newTagDraft && newTagDraft.trim()) {
                            e.preventDefault();
                            const label = newTagDraft.trim();
                            const color = newTagColor || undefined;
                            setNewTagDraft(null);
                            setNewTagColor('');
                            setTagMenuOpen(false);
                            await onCreateAndLinkTag(label, color);
                          } else if (e.key === 'Escape') {
                            setNewTagDraft(null);
                          }
                        }}
                      />
                      <input
                        type="color"
                        value={newTagColor || '#cbd5f5'}
                        onChange={(e) => setNewTagColor(e.target.value)}
                        className="h-6 w-7 cursor-pointer rounded-sm border border-rule-strong"
                        title="Couleur du tag (optionnel)"
                      />
                    </div>
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => setNewTagDraft(null)}
                        className="rounded px-1.5 py-0.5 text-[11px] text-text-tertiary hover:bg-paper-subtle"
                      >
                        Annuler
                      </button>
                      <button
                        onClick={async () => {
                          if (newTagDraft && newTagDraft.trim()) {
                            const label = newTagDraft.trim();
                            const color = newTagColor || undefined;
                            setNewTagDraft(null);
                            setNewTagColor('');
                            setTagMenuOpen(false);
                            await onCreateAndLinkTag(label, color);
                          }
                        }}
                        disabled={!newTagDraft || !newTagDraft.trim()}
                        className="rounded bg-accent-light px-2 py-0.5 text-[11px] font-medium text-accent-text disabled:opacity-50"
                      >
                        Créer
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-2 flex items-center gap-1 text-[11px] text-text-tertiary">
        <span className="font-mono">
          PV n° {pdfFilenameRef}, p. {a.page_number}
        </span>
      </div>
      {cotation && (
        <div className="mt-1 text-[12px] italic text-text-secondary">
          {cotation} —{' '}
          <span
            className="font-mono text-[10px] uppercase tracking-[0.3px] text-text-tertiary"
            title={a.creator_display_name ?? 'auteur inconnu'}
          >
            {initials}
          </span>{' '}
          · {relativeAge(a.created_at)}
        </div>
      )}
      {!cotation && (
        <div className="mt-1 text-[11px] text-text-tertiary">
          <span title={a.creator_display_name ?? 'auteur inconnu'}>{initials}</span> ·{' '}
          {relativeAge(a.created_at)} · v{a.current_version}
        </div>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// PieceNoteCard — free-form note attached to the current piece
// (Sprint 1ter++/ADR-032). Visually distinct from annotations: dashed
// indigo accent border + no category strip.
// ---------------------------------------------------------------------------

function PieceNoteCard({
  note,
  onUpdate,
  onDelete,
  onReorder,
}: {
  note: import('../lib/types').DossierNote;
  onUpdate: (newContent: string) => Promise<void>;
  onDelete: () => Promise<void>;
  onReorder: (draggedId: string, targetId: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.content);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const initials = toInitials(note.creator_display_name);

  const save = async () => {
    setBusy(true);
    try {
      await onUpdate(draft.trim());
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <li
      draggable={!editing}
      onDragStart={(e) => {
        if (editing) return;
        e.dataTransfer.setData('text/plain', note.id);
        e.dataTransfer.setData(noteScopeMime(note), '');
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragOver={(e) => {
        if (!e.dataTransfer.types.includes('text/plain')) return;
        if (!e.dataTransfer.types.includes(noteScopeMime(note))) {
          e.dataTransfer.dropEffect = 'none';
          return;
        }
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (!dragOver) setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const draggedId = e.dataTransfer.getData('text/plain');
        if (draggedId && draggedId !== note.id) void onReorder(draggedId, note.id);
      }}
      className={`group rounded-md border border-dashed border-accent/60 bg-accent-lighter px-3.5 py-3 transition cursor-grab active:cursor-grabbing ${
        dragOver ? 'border-accent ring-2 ring-accent/40' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-[10px] font-semibold uppercase tracking-[1px] text-accent-text">
          NOTE LIBRE
          {note.page_number != null && (
            <span className="ml-1.5 font-mono text-text-tertiary">· p. {note.page_number}</span>
          )}
        </div>
        {confirmDel ? (
          <div className="flex items-center gap-1 text-[11px]">
            <Btn variant="ghost" size="sm" onClick={() => setConfirmDel(false)} disabled={busy}>
              Annuler
            </Btn>
            <button
              onClick={async () => {
                setBusy(true);
                await onDelete();
              }}
              disabled={busy}
              className="rounded-md bg-cat-def px-2 py-1 text-[11px] font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              Supprimer
            </button>
          </div>
        ) : !editing ? (
          <span className="flex items-center gap-1 opacity-0 transition group-hover:opacity-100">
            <button
              onClick={() => {
                setDraft(note.content);
                setEditing(true);
              }}
              className="rounded p-1 text-text-tertiary hover:bg-paper-subtle hover:text-text-primary"
              title="Modifier"
            >
              ✎
            </button>
            <button
              onClick={() => setConfirmDel(true)}
              className="rounded p-1 text-text-tertiary hover:bg-cat-def-pastel hover:text-cat-def"
              title="Supprimer"
            >
              ✕
            </button>
          </span>
        ) : null}
      </div>

      {editing ? (
        <div className="mt-2 space-y-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            autoFocus
            placeholder="Note libre (clic 🎤 pour dicter)…"
            className="w-full rounded-sm border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[13px]"
          />
          <div className="flex items-center justify-between gap-1">
            <DictationButton
              size="sm"
              onTranscript={(text) =>
                setDraft((prev) => (prev ? prev.trimEnd() + ' ' : '') + text)
              }
              title="Dicter la note"
            />
            <div className="flex gap-1">
              <Btn variant="ghost" size="sm" onClick={() => setEditing(false)} disabled={busy}>
                Annuler
              </Btn>
              <Btn variant="primary" size="sm" onClick={() => void save()} disabled={busy}>
                {busy ? '…' : 'Enregistrer'}
              </Btn>
            </div>
          </div>
        </div>
      ) : (
        <p className="mt-1.5 whitespace-pre-wrap text-[13px] leading-relaxed text-text-primary">
          {note.content}
        </p>
      )}

      <div className="mt-2 text-[11px] text-text-tertiary">
        <span title={note.creator_display_name ?? 'auteur inconnu'}>{initials}</span> ·{' '}
        {relativeAge(note.created_at)}
      </div>
    </li>
  );
}
